/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
    rtiAttachedEvent,
    samAttachedEvent,
    sendUsbDeviceMute,
    sendUsbDeviceMuteFailed,
    sendUsbDeviceMuteSuccess,
    sendUsbDeviceVolume,
    sendUsbDeviceVolumeFailed,
    sendUsbDeviceVolumeSuccess,
    usbDeviceInfoEvent,
    usbEffectsInitialized
} from './usb.actions';
import { updateMediaDevices } from '../../call/+state/media.actions';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { SamAudioService } from '../sam/services/sam-audio.service';
import { SamService } from '../sam/services/sam.service';
import { from, of } from 'rxjs';
import { RTIService } from '../rti/services/rti.service';
import { UsbBaseService } from '../shared/services/usb-base.service';
import { acdAnswerRequestSuccess } from '../../user/+state/user.actions';
import { answerSuccess, irrPlayback } from '../../call/+state/call.actions';
import { concatLatestFrom } from '@ngrx/operators';
import { selectConnectedUsbCallAudioInputs, selectRtiInitialized, selectSamInitialized } from './usb.selectors';
import { JackboxMuteMessage } from '../shared/model/report-1/control/jackbox-mute-message';
import { VolumeMessage } from '../shared/model/report-1/control/volume-message';
import { PlaybackAudioMessage } from '../shared/model/report-1/control/playback-audio-message';
import { RtiAudioService } from '../rti/services/rti-audio.service';
import { EnvironmentService } from '../../core/services/environment.service';
import { SamFirmwareUpgradeService } from '../sam/services/sam-firmware-upgrade.service';
import { RtiFirmwareUpgradeService } from '../rti/services/rti-firmware-upgrade.service';
import { displayTimedToastNotification } from '../../notification/+state/notification.actions';
import { ToastType } from '@msi/cobalt';
import { Consts } from '../shared/util/consts';
import { UserService } from '../../user/services/user.service';

@Injectable()
export class UsbEffects implements OnInitEffects {
    private usbService: UsbBaseService;
    constructor(
        private samService: SamService,
        private samFirmwareUpgradeService: SamFirmwareUpgradeService,
        private rtiService: RTIService,
        private rtiFirmwareUpgradeService: RtiFirmwareUpgradeService,
        private actions$: Actions,
        private store: Store,
        private env: EnvironmentService,
        private userService: UserService
    ) {
        this.store.select(selectRtiInitialized).pipe(filter((i) => i)).subscribe(() => {
            this.usbService = this.rtiService;
            this.rtiFirmwareUpgradeService.initialize();
        });
        this.store.select(selectSamInitialized).pipe(filter((i) => i)).subscribe(() => {
            this.usbService = this.samService;
            this.samFirmwareUpgradeService.initialize();
        });
    }

    ngrxOnInitEffects(): Action {
        return usbEffectsInitialized();
    }

    samAttached$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateMediaDevices),
            filter(() => !this.env.environment.featureFlags?.ccAdminIntegrated || this.env.environment.featureFlags?.enableSAM),
            map(({ devices }) => samAttachedEvent({ isAttached: devices.findIndex((device) =>
                    device.label.includes(SamAudioService.SAM_INPUT_DEVICE_LABEL) ||
                    device.label.includes(SamAudioService.SAM_OUTPUT_DEVICE_LABEL)) >= 0 }))
        )
    );

    rtiAttached$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateMediaDevices),
            filter(() => !this.env.environment.featureFlags?.ccAdminIntegrated || this.env.environment.featureFlags?.enableRTI),
            map(({ devices }) => rtiAttachedEvent({ isAttached: devices.findIndex((device) =>
                    device.label.includes(RtiAudioService.RTI_INPUT_DEVICE_LABEL) ||
                    device.label.includes(RtiAudioService.RTI_OUTPUT_DEVICE_LABEL)) >= 0 }))
        )
    );

    muteUsbDeviceAction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sendUsbDeviceMute),
            filter(() => Boolean(this.usbService)),
            switchMap(({ muteId, muted }) =>
                from(this.usbService.sendReport1(new JackboxMuteMessage()
                    // @ts-ignore
                    .withDevice(JackboxMuteMessage.DEVICE[muteId])
                    .withState(muted ? JackboxMuteMessage.STATE.MUTE : JackboxMuteMessage.STATE.UNMUTE))
                ).pipe(
                    map(() => sendUsbDeviceMuteSuccess()),
                    catchError((err: Error) => of(sendUsbDeviceMuteFailed({ payload: err.message })))
                )
            )
        )
    );

    volumeUsbDeviceAction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sendUsbDeviceVolume),
            filter(() => Boolean(this.usbService)),
            switchMap(({ volumeId, volume }) =>
                from(this.usbService.sendReport1(new VolumeMessage()
                    // @ts-ignore
                    .withDevice(VolumeMessage.VOLUME_DEVICE_ID[volumeId])
                    .withVolume(volume))
                ).pipe(
                    map(() => sendUsbDeviceVolumeSuccess()),
                    catchError((err: Error) => of(sendUsbDeviceVolumeFailed({ payload: err.message })))
                )
            )
        )
    );

    muteOnAnswer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(acdAnswerRequestSuccess, answerSuccess),
            concatLatestFrom(() => this.store.select(selectConnectedUsbCallAudioInputs)),
            mergeMap(([, inputs]) => inputs.map((input) => sendUsbDeviceMute({ muteId: input.muteId, muted: input.muteOnAnswer })))
        )
    );

    irrPlayback$ = createEffect(() =>
        this.actions$.pipe(
            ofType(irrPlayback),
            filter(() => Boolean(this.usbService)),
            map(({ isPlaying}) => this.usbService.sendReport1(new PlaybackAudioMessage()
                .withState( isPlaying ? PlaybackAudioMessage.STATE.BEGIN : PlaybackAudioMessage.STATE.END)))
        ),
        { dispatch: false }
    );

    positionConfiguration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(usbDeviceInfoEvent),
            filter(() => !localStorage.getItem(Consts.BROWSER_IDENTIFIER)),
            map(({ serial }) => {
                localStorage.setItem(Consts.BROWSER_IDENTIFIER, serial);
                setTimeout(() => this.userService.logout(), Consts.RELOAD_MILLISECONDS);
                return displayTimedToastNotification({
                    level: ToastType.error,
                    message: `Configuring position. You must re-authenticate in`,
                    options: { autoDismiss: false },
                    countdown: Consts.RELOAD_MILLISECONDS,
                    ref: 'positionReload'
                });
            })
        )
    );
}
